// YNX — Recovery tab for the BusinessOnboarding form.
// Lets a user retrieve workspaces owned by their e-mail when they have lost
// their localStorage AND forgotten their YNX-XXXXXX access code.
//
// Backend contract: POST /api/biz/workspace/recover_by_email
//   body  : {email}
//   returns: {count, workspaces:[{id, name, access_code, plan, created_at}]}
//   limits: 8 attempts / 24h / email (429 above)
import React, { useState } from 'react';
import { Building2, ArrowRight, Mail } from 'lucide-react';
import { recoverByEmail, joinWorkspace, setLocalWorkspace } from '../../lib/biz';
import { track } from '../../lib/tracker';

export const OnboardRecoverTab = ({ onReady, onError }) => {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);   // {count, workspaces[]} | null

  const handleRecover = async () => {
    const e = email.trim().toLowerCase();
    if (!e || !e.includes('@')) { onError?.('Adresse e-mail invalide.'); return; }
    setBusy(true); setResult(null); onError?.('');
    try {
      const res = await recoverByEmail(e);
      track('onboard_recover', { count: res.count });
      setResult(res);
      if (res.count === 0) {
        onError?.("Aucun espace trouvé pour cette adresse. Vérifiez l'orthographe ou créez un nouvel espace.");
      } else if (res.email_sent) {
        onError?.('');   // clear any prior error
      }
    } catch (err) {
      track('onboard_recover_error', { msg: String(err?.message || '').slice(0, 60) });
      onError?.(err.message || 'Erreur lors de la recherche.');
    } finally { setBusy(false); }
  };

  const enter = async (ws) => {
    setBusy(true); onError?.('');
    try {
      const full = await joinWorkspace(ws.access_code);
      setLocalWorkspace(full.id);
      track('signup', { mode: 'recover', code: ws.access_code });
      onReady(full);
    } catch (err) {
      onError?.('Impossible d\'ouvrir cet espace : ' + (err.message || ''));
    } finally { setBusy(false); }
  };

  return (
    <>
      <p className='biz-recover-help' data-testid='biz-recover-help'>
        <Mail size={13} style={{ verticalAlign: '-2px' }} /> Entrez l&apos;e-mail
        utilisé lors de la création de votre espace pour retrouver votre
        code d&apos;accès.
      </p>
      <label className='biz-label'>E-mail du gérant</label>
      <input
        className='biz-input'
        name='recover-email'
        type='email'
        placeholder='contact@entreprise.fr'
        value={email}
        onChange={(ev) => setEmail(ev.target.value)}
        onKeyDown={(ev) => ev.key === 'Enter' && handleRecover()}
        autoFocus
        data-testid='biz-recover-email'
      />
      <button
        className='biz-btn-primary'
        disabled={busy}
        onClick={handleRecover}
        data-testid='biz-recover-btn'
      >
        {busy ? 'Recherche…' : 'Retrouver mes espaces'}
      </button>

      {result && result.count > 0 && result.email_sent && (
        <div className='biz-recover-sent' data-testid='biz-recover-sent'>
          <div className='biz-recover-sent-icon'>📧</div>
          <div>
            <strong>{result.message || `Code envoyé à ${email}`}</strong>
            <p className='biz-recover-sent-sub'>
              Ouvrez votre boîte e-mail (et le dossier « Spam »), puis revenez
              ici sur l&apos;onglet <b>« Rejoindre »</b> avec votre code YNX-XXXXXX.
            </p>
          </div>
        </div>
      )}

      {result && result.count > 0 && !result.email_sent && (
        <div className='biz-recover-results' data-testid='biz-recover-results'>
          <div className='biz-recover-results-head'>
            {result.count === 1
              ? '1 espace trouvé :'
              : `${result.count} espaces trouvés :`}
          </div>
          {result.workspaces.map((ws) => (
            <article key={ws.id} className='biz-recover-card' data-testid={`biz-recover-ws-${ws.id}`}>
              <div className='biz-recover-card-head'>
                <Building2 size={14} />
                <span className='biz-recover-name'>{ws.name}</span>
                <span className={`biz-recover-plan plan-${ws.plan}`}>{ws.plan}</span>
              </div>
              <div className='biz-recover-code'>{ws.access_code}</div>
              <button
                className='biz-btn-primary sm'
                disabled={busy}
                onClick={() => enter(ws)}
                data-testid={`biz-recover-enter-${ws.id}`}
              >
                <ArrowRight size={12} /> Entrer dans cet espace
              </button>
            </article>
          ))}
          <p className='biz-recover-note'>
            ⚠ L&apos;envoi par e-mail a échoué — voici vos codes en clair.
            Configurez Resend correctement pour un meilleur niveau de sécurité.
          </p>
        </div>
      )}
    </>
  );
};

export default OnboardRecoverTab;
